import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/shared/Layout/Layout';
import Loading from '../../components/shared/Loading/Loading';
import Button from '../../components/shared/Button/Button';
import Modal from '../../components/shared/Modal/Modal';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  FormHint,
  FormGrid,
  FormActions,
  ErrorMessage,
} from '../../components/shared/Form/Form.styles';
import CurpField from '../../components/sales/CurpField';
import SaleTicket from '../../components/sales/SaleTicket';
import SalesNav from '../../components/sales/SalesNav';
import { PrivacyBox } from '../../components/sales/sales.styles';
import {
  PAYMENT_METHODS,
  checkAvailability,
  createSale,
  listSalePromoters,
  listSellableActivities,
  paymentMethodLabel,
} from '../../services/salesApi';
import { StaffSessionError } from '../../services/staffApi';
import {
  PRIVACY_CHECKBOX_LABEL,
  privacyNoticeParagraphs,
} from '../../content/privacyNoticeCurp';
import { maskCurp } from '../../utils/curp';
import { formatMoney } from '../../utils/money';
import { formatCalendarDate, nowTimeInMexico, todayInMexico } from '../../utils/dates';
import useSalesAccess from './useSalesAccess';
import SalesGate from './SalesGate';
import {
  SalesContainer,
  PageHead,
  Panel,
  Notice,
  DetailList,
  IvaTag,
  MoneyTag,
} from './Sales.styles';
import {
  SeatsRow,
  AttendeeRow,
  MethodGroup,
  TotalBox,
  AvailabilityLine,
  ReviewList,
} from './Sell.styles';

const DEFAULT_MAX_QUANTITY = 50;

// Traduce un ApiError de POST /api/empresa/sales a lo que la pantalla debe hacer.
// Devuelve { message, attendee?: número (1-based), reload?: bool, refreshStaff?: bool }.
const describeSaleError = (error) => {
  const data = error.data || {};
  const attendeeMatch = /asistente #(\d+)/i.exec(error.message || '');
  const attendee = attendeeMatch ? Number(attendeeMatch[1]) : undefined;

  switch (error.code) {
    case 'no_availability':
      return {
        message: `${error.message}${typeof data.remainingCapacity === 'number' ? ` (quedan ${data.remainingCapacity} lugares)` : ''}`,
      };
    case 'activity_not_active':
      return { message: error.message, reload: true, clearActivity: true };
    case 'curp_required':
    case 'curp_invalid':
      return { message: error.message, attendee };
    case 'curp_not_required':
      return { message: error.message, reload: true };
    case 'privacy_notice_required':
      return { message: error.message };
    case 'curp_encryption_unavailable':
      return { message: error.message };
    case 'promoter_invalid':
      // el promotor ya no es elegible (revocado, suspendido...): no se consumió cupo ni ticket
      return { message: error.message, reloadPromoters: true, clearPromoter: true };
    default:
      break;
  }

  if (error.status === 403) return { message: error.message, reload: true, refreshStaff: true };
  if (error.status === 404) {
    return { message: 'La actividad ya no existe o no está disponible para ti.', reload: true, clearActivity: true };
  }
  if (error.status === 429) {
    return { message: 'Demasiadas peticiones seguidas. Espera un momento e inténtalo de nuevo.' };
  }
  if (error.status === 401) {
    return { message: 'Tu sesión venció. Inicia sesión de nuevo para continuar.' };
  }
  if (attendee) return { message: error.message, attendee };
  return { message: error.message };
};

const emptyCustomer = { name: '', email: '', phone: '' };

const SellForm = ({ access }) => {
  const { mode, isStaff, businessName, staffAuth } = access;
  const { endSession, refresh: refreshStaff } = staffAuth;
  const base = isStaff ? '/staff' : '/empresa';

  // --- configuración que manda el backend --------------------------------
  const [config, setConfig] = useState(null); // { activities, paymentMethods, maxQuantity, curpAvailable }
  const [loadError, setLoadError] = useState('');

  // --- formulario ----------------------------------------------------------
  const [activityId, setActivityId] = useState('');
  const [date, setDate] = useState(todayInMexico);
  const [time, setTime] = useState('');
  const [seats, setSeats] = useState(1);
  const [names, setNames] = useState(['']);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [promoters, setPromoters] = useState([]); // [{ id, name, kind }] elegibles
  const [promoterId, setPromoterId] = useState('');
  // Los CURP NO están en el estado: cada CurpField guarda el suyo en su <input>.
  const curpRefs = useRef([]);
  const [curpErrors, setCurpErrors] = useState({}); // { índice: mensaje del servidor }
  const [formKey, setFormKey] = useState(0); // cambia al empezar otra venta

  const [formError, setFormError] = useState('');
  const [availability, setAvailability] = useState(null); // { tone, text }
  const [review, setReview] = useState(null); // { masked: [...] } mientras se confirma
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { sale, qrCode, remainingCapacity, message }
  const errorRef = useRef(null);

  const handleSessionError = useCallback((error) => {
    if (error instanceof StaffSessionError) {
      endSession(error.message);
      return true;
    }
    return false;
  }, [endSession]);

  const loadConfig = useCallback(async () => {
    setLoadError('');
    try {
      const data = await listSellableActivities(mode);
      setConfig({
        activities: data.activities || [],
        paymentMethods: data.paymentMethods && data.paymentMethods.length
          ? data.paymentMethods
          : Object.keys(PAYMENT_METHODS),
        maxQuantity: data.maxQuantity || DEFAULT_MAX_QUANTITY,
        curpAvailable: data.curpAvailable !== false,
      });
    } catch (error) {
      if (handleSessionError(error)) return;
      setLoadError(error.message);
    }
  }, [mode, handleSessionError]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Promotores elegibles (opcional): si falla, simplemente no se ofrece el selector.
  const loadPromoters = useCallback(async () => {
    try {
      const data = await listSalePromoters(mode);
      setPromoters(data.promoters || []);
    } catch (error) {
      if (handleSessionError(error)) return;
      setPromoters([]);
    }
  }, [mode, handleSessionError]);

  useEffect(() => { loadPromoters(); }, [loadPromoters]);

  const promoter = promoters.find((p) => String(p.id) === String(promoterId)) || null;

  const activity = useMemo(
    () => (config ? config.activities.find((a) => String(a.id) === String(activityId)) : null) || null,
    [config, activityId]
  );
  const requireCurp = Boolean(activity && activity.requireCurp);
  const slots = (activity && activity.timeSlots) || [];
  const maxQuantity = config ? config.maxQuantity : DEFAULT_MAX_QUANTITY;
  const unitPrice = activity ? Number(activity.price) || 0 : 0;
  const total = unitPrice * seats;

  // --- cambios de actividad / lugares -----------------------------------

  const handleActivityChange = (event) => {
    const id = event.target.value;
    setActivityId(id);
    setFormError('');
    setCurpErrors({});
    const next = config.activities.find((a) => String(a.id) === String(id));
    if (!next) {
      setTime('');
      return;
    }
    const nextSlots = next.timeSlots || [];
    setTime(nextSlots.length > 0 ? nextSlots[0].time : nowTimeInMexico());
  };

  const changeSeats = (value) => {
    const n = Math.max(1, Math.min(maxQuantity, Number(value) || 1));
    setSeats(n);
    setNames((current) => {
      const next = current.slice(0, n);
      while (next.length < n) next.push('');
      return next;
    });
    setCurpErrors({});
  };

  // --- disponibilidad (solo informativa) --------------------------------

  useEffect(() => {
    if (!activityId || !date || !time) {
      setAvailability(null);
      return undefined;
    }
    let cancelled = false;
    setAvailability({ tone: 'neutral', text: 'Revisando disponibilidad…' });
    const timer = setTimeout(async () => {
      const data = await checkAvailability(activityId, date, time, seats);
      if (cancelled) return;
      if (!data) {
        setAvailability(null);
      } else if (data.success && data.available) {
        setAvailability({
          tone: 'good',
          text: typeof data.remainingCapacity === 'number'
            ? `Hay lugar. Cupo restante: ${data.remainingCapacity}.`
            : 'Hay lugar para esa fecha y horario.',
        });
      } else {
        setAvailability({
          tone: 'bad',
          text: data.remainingCapacity > 0
            ? `Solo quedan ${data.remainingCapacity} lugares para esa fecha y horario.`
            : data.message || 'No hay disponibilidad para esa fecha y horario.',
        });
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activityId, date, time, seats]);

  // --- revisar y confirmar -----------------------------------------------

  const showError = (message) => {
    setFormError(message);
    setTimeout(() => {
      if (errorRef.current && errorRef.current.scrollIntoView) {
        errorRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 0);
  };

  const clearCurps = () => {
    curpRefs.current.forEach((field) => field && field.clear && field.clear());
  };

  const handleReview = (event) => {
    event.preventDefault();
    setFormError('');
    setCurpErrors({});

    if (!activity) {
      showError('Elige la actividad que vas a vender.');
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(time)) {
      showError('Elige el horario.');
      return;
    }

    let masked = null;
    if (requireCurp) {
      masked = [];
      let firstBad = -1;
      for (let i = 0; i < seats; i += 1) {
        const field = curpRefs.current[i];
        const filled = field && field.getValue();
        if (!filled || !field.validate()) {
          if (firstBad === -1) firstBad = i;
          if (!filled) setCurpErrors((c) => ({ ...c, [i]: 'Escribe el CURP de este asistente.' }));
        } else {
          masked.push(maskCurp(field.getValue()));
        }
      }
      if (firstBad !== -1) {
        showError(`Revisa el CURP del asistente #${firstBad + 1}.`);
        if (curpRefs.current[firstBad]) curpRefs.current[firstBad].focus();
        return;
      }
    }

    setAccepted(false);
    setReview({ masked });
  };

  const handleConfirm = async () => {
    if (!review || submitting) return;
    if (requireCurp && !accepted) return;

    const attendees = names.map((name, i) => {
      const entry = { name: name.trim() };
      if (requireCurp) entry.curp = curpRefs.current[i].getValue();
      return entry;
    });

    const payload = {
      activityId: Number(activityId),
      time,
      date,
      attendees,
      quantity: seats,
      paymentMethod,
    };
    const customerData = {};
    if (customer.name.trim()) customerData.name = customer.name.trim();
    if (customer.email.trim()) customerData.email = customer.email.trim();
    if (customer.phone.trim()) customerData.phone = customer.phone.trim();
    if (Object.keys(customerData).length) payload.customer = customerData;
    if (notes.trim()) payload.notes = notes.trim();
    if (requireCurp) payload.privacyNoticeAccepted = true;
    if (promoter) payload.promoterId = Number(promoter.id);

    setSubmitting(true);
    try {
      const data = await createSale(mode, payload);
      // El CURP ya no hace falta en pantalla: se borra de los campos.
      clearCurps();
      setReview(null);
      setResult({
        sale: data.sale,
        qrCode: data.qrCode,
        remainingCapacity: data.remainingCapacity,
        message: data.message,
      });
      window.scrollTo({ top: 0 });
    } catch (error) {
      if (handleSessionError(error)) return;
      const info = describeSaleError(error);
      setReview(null);
      showError(info.message);
      if (info.attendee) {
        setCurpErrors({ [info.attendee - 1]: info.message });
      }
      if (info.clearActivity) setActivityId('');
      if (info.clearPromoter) setPromoterId('');
      if (info.reloadPromoters) loadPromoters();
      if (info.reload) loadConfig();
      if (info.refreshStaff && isStaff) refreshStaff().catch(() => {});
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    clearCurps();
    setResult(null);
    setSeats(1);
    setNames(['']);
    setCustomer(emptyCustomer);
    setNotes('');
    setPaymentMethod('cash');
    setPromoterId('');
    setCurpErrors({});
    setFormError('');
    setReview(null);
    setFormKey((k) => k + 1);
    loadConfig();
    loadPromoters();
  };

  // --- render ---------------------------------------------------------------

  if (result) {
    return (
      <Layout headerType={access.headerType}>
        <SalesContainer>
          <SalesNav mode={mode} />
          <Notice role="status">
            <strong>{result.message || `Venta registrada. Ticket #${result.sale.ticketNumber}`}</strong>
            {typeof result.remainingCapacity === 'number' && (
              <> Quedan {result.remainingCapacity} lugares para ese horario.</>
            )}
          </Notice>
          <SaleTicket sale={result.sale} qrImage={result.qrCode}>
            <FormActions>
              <Link to={`${base}/sales`}><Button variant="ghost">Ver {isStaff ? 'mis ventas' : 'ventas'}</Button></Link>
              <Button onClick={startAnother}>Nueva venta</Button>
            </FormActions>
          </SaleTicket>
        </SalesContainer>
      </Layout>
    );
  }

  const noActivities = config && config.activities.length === 0;

  return (
    <Layout headerType={access.headerType}>
      <SalesContainer>
        <PageHead>
          <h1>Vender en persona</h1>
          <p>Registra una venta cobrada en el lugar y entrega el QR al cliente.</p>
        </PageHead>
        <SalesNav mode={mode} />

        {!config && !loadError && <Loading text="Cargando actividades..." />}

        {loadError && (
          <Panel>
            <ErrorMessage role="alert">{loadError}</ErrorMessage>
            <Button onClick={loadConfig}>Reintentar</Button>
          </Panel>
        )}

        {noActivities && (
          <Panel>
            <h2>No hay actividades para vender</h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              {isStaff
                ? 'No tienes actividades activas asignadas. Pide a tu empresa que te asigne alguna.'
                : 'Necesitas al menos una actividad activa. Publícala desde la sección Actividades.'}
            </p>
          </Panel>
        )}

        {config && !noActivities && (
          <form onSubmit={handleReview} key={formKey}>
            {formError && (
              <div ref={errorRef}>
                <ErrorMessage role="alert">{formError}</ErrorMessage>
              </div>
            )}

            <Panel>
              <h2>1. Actividad, fecha y horario</h2>
              <FormGroup>
                <FormLabel htmlFor="sale-activity">Actividad</FormLabel>
                <FormSelect id="sale-activity" value={activityId} onChange={handleActivityChange} required>
                  <option value="">Elige una actividad…</option>
                  {config.activities.map((a) => {
                    const blocked = a.requireCurp && !config.curpAvailable;
                    return (
                      <option key={a.id} value={a.id} disabled={blocked}>
                        {a.title} · {formatMoney(a.price)}{a.requireCurp ? ' · pide CURP' : ''}{blocked ? ' (CURP no disponible)' : ''}
                      </option>
                    );
                  })}
                </FormSelect>
                {!config.curpAvailable && (
                  <FormHint>
                    El registro de CURP no está disponible por ahora; las actividades que lo piden no se pueden vender. Contacta a soporte.
                  </FormHint>
                )}
              </FormGroup>

              {activity && (
                <p style={{ marginBottom: 14, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                  Precio por lugar: <MoneyTag>{formatMoney(unitPrice)}</MoneyTag> <IvaTag>IVA incluido</IvaTag>
                  {activity.location ? ` · ${activity.location}` : ''}
                  {activity.duration ? ` · ${activity.duration}` : ''}
                </p>
              )}

              <FormGrid>
                <FormGroup>
                  <FormLabel htmlFor="sale-date">Fecha de la actividad</FormLabel>
                  <FormInput
                    id="sale-date"
                    type="date"
                    value={date}
                    min={todayInMexico()}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                  <FormHint>Hoy es {formatCalendarDate(todayInMexico())} (hora de México).</FormHint>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="sale-time">Horario</FormLabel>
                  {slots.length > 0 ? (
                    <FormSelect id="sale-time" value={time} onChange={(e) => setTime(e.target.value)} required>
                      {slots.map((s) => <option key={s.time} value={s.time}>{s.time}</option>)}
                    </FormSelect>
                  ) : (
                    <FormInput
                      id="sale-time"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      disabled={!activity}
                      required
                    />
                  )}
                </FormGroup>
              </FormGrid>
              {availability && <AvailabilityLine $tone={availability.tone} aria-live="polite">{availability.text}</AvailabilityLine>}
            </Panel>

            <Panel>
              <h2>2. Asistentes</h2>
              <FormGroup>
                <FormLabel id="seats-label">Lugares</FormLabel>
                <SeatsRow role="group" aria-labelledby="seats-label">
                  <button type="button" onClick={() => changeSeats(seats - 1)} disabled={seats <= 1} aria-label="Quitar un lugar">−</button>
                  <output aria-live="polite">{seats}</output>
                  <button type="button" onClick={() => changeSeats(seats + 1)} disabled={seats >= maxQuantity} aria-label="Agregar un lugar">+</button>
                </SeatsRow>
                <FormHint>Un asistente con nombre por lugar (máximo {maxQuantity}).</FormHint>
              </FormGroup>

              {requireCurp && (
                <Notice $warn>
                  Esta actividad pide el <strong>CURP</strong> de cada asistente. Se muestra oculto, se guarda cifrado
                  y antes de cobrar se le pedirá al cliente aceptar el aviso de privacidad.
                </Notice>
              )}

              {names.map((name, i) => (
                <AttendeeRow key={i}>
                  <legend>Asistente {i + 1}</legend>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <FormLabel htmlFor={`attendee-name-${i}`}>Nombre completo</FormLabel>
                    <FormInput
                      id={`attendee-name-${i}`}
                      value={name}
                      maxLength={100}
                      autoComplete="off"
                      required
                      onChange={(e) => {
                        const value = e.target.value;
                        setNames((current) => current.map((n, idx) => (idx === i ? value : n)));
                      }}
                    />
                  </FormGroup>
                  {requireCurp && (
                    <CurpField
                      id={`attendee-curp-${i}`}
                      label="CURP"
                      ref={(el) => { curpRefs.current[i] = el; }}
                      serverError={curpErrors[i]}
                    />
                  )}
                </AttendeeRow>
              ))}
            </Panel>

            <Panel>
              <h2>3. Cliente <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>(opcional)</span></h2>
              <FormGroup>
                <FormLabel htmlFor="customer-name">Nombre del cliente</FormLabel>
                <FormInput
                  id="customer-name"
                  value={customer.name}
                  maxLength={100}
                  autoComplete="off"
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                />
                <FormHint>Si lo dejas vacío se usa el nombre del primer asistente.</FormHint>
              </FormGroup>
              <FormGrid>
                <FormGroup>
                  <FormLabel htmlFor="customer-email">Email</FormLabel>
                  <FormInput
                    id="customer-email"
                    type="email"
                    value={customer.email}
                    autoComplete="off"
                    onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="customer-phone">Teléfono</FormLabel>
                  <FormInput
                    id="customer-phone"
                    type="tel"
                    value={customer.phone}
                    maxLength={20}
                    autoComplete="off"
                    onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  />
                </FormGroup>
              </FormGrid>
            </Panel>

            <Panel>
              <h2>4. Pago</h2>
              <FormGroup>
                <FormLabel id="method-label">Método de pago</FormLabel>
                <MethodGroup role="radiogroup" aria-labelledby="method-label">
                  {config.paymentMethods.map((method) => (
                    <label key={method}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method}
                        checked={paymentMethod === method}
                        onChange={() => setPaymentMethod(method)}
                      />
                      <span>{paymentMethodLabel(method)}</span>
                    </label>
                  ))}
                </MethodGroup>
              </FormGroup>

              {promoters.length > 0 && (
                <FormGroup>
                  <FormLabel htmlFor="sale-promoter">Promotor <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></FormLabel>
                  <FormSelect id="sale-promoter" value={promoterId} onChange={(e) => setPromoterId(e.target.value)}>
                    <option value="">Sin promotor</option>
                    {promoters.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.kind === 'empresa' ? 'empresa' : 'persona'})</option>
                    ))}
                  </FormSelect>
                  <FormHint>Si esta venta la trajo un promotor, elígelo para registrar su comisión. El precio no cambia.</FormHint>
                </FormGroup>
              )}

              <FormGroup>
                <FormLabel htmlFor="sale-notes">Notas <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></FormLabel>
                <FormTextarea
                  id="sale-notes"
                  value={notes}
                  maxLength={500}
                  rows={2}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <FormHint>{notes.length}/500</FormHint>
              </FormGroup>

              <TotalBox>
                <span className="label">Total a cobrar ({seats} {seats === 1 ? 'lugar' : 'lugares'})</span>
                <span className="amount">{formatMoney(total)}</span>
                <span className="sub">IVA incluido · sin cargo por servicio de BokerHub</span>
              </TotalBox>

              <FormActions>
                <Button type="submit" disabled={!activity || submitting}>
                  Revisar y cobrar
                </Button>
              </FormActions>
            </Panel>
          </form>
        )}
      </SalesContainer>

      {review && (
        <Modal
          title="Confirmar venta"
          onClose={submitting ? null : () => setReview(null)}
          maxWidth="560px"
        >
          <ReviewList>
            <DetailList>
              <div className="row"><dt>Actividad</dt><dd>{activity.title}</dd></div>
              <div className="row"><dt>Fecha y hora</dt><dd>{formatCalendarDate(date)} · {time}</dd></div>
              <div className="row"><dt>Lugares</dt><dd>{seats}</dd></div>
              <div className="row"><dt>Método de pago</dt><dd>{paymentMethodLabel(paymentMethod)}</dd></div>
              {customer.name.trim() && <div className="row"><dt>Cliente</dt><dd>{customer.name.trim()}</dd></div>}
              {promoter && <div className="row"><dt>Promotor</dt><dd>{promoter.name}</dd></div>}
              <div className="row strong">
                <dt>Total a cobrar</dt>
                <dd><MoneyTag>{formatMoney(total)}</MoneyTag> <IvaTag>IVA incluido</IvaTag></dd>
              </div>
            </DetailList>

            <h3 style={{ fontSize: 'var(--text-base)', margin: '14px 0 4px' }}>Asistentes</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 'var(--text-sm)' }}>
              {names.map((name, i) => (
                <li key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
                  <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{i + 1}. {name.trim()}</span>
                  {review.masked && (
                    <span style={{ color: 'var(--text-muted)', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                      {review.masked[i]}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </ReviewList>

          {requireCurp && (
            <PrivacyBox>
              <h3>Aviso de privacidad</h3>
              <div className="notice-text" tabIndex={0} role="region" aria-label="Texto del aviso de privacidad">
                {privacyNoticeParagraphs(businessName).map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              <label className="accept">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span>{PRIVACY_CHECKBOX_LABEL}</span>
              </label>
              <p className="draft">Léelo o muéstralo al cliente antes de continuar. No se guarda ningún CURP hasta que lo acepte.</p>
            </PrivacyBox>
          )}

          <FormActions>
            <Button type="button" variant="ghost" onClick={() => setReview(null)} disabled={submitting}>
              Volver
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={submitting || (requireCurp && !accepted)}
            >
              {submitting ? 'Registrando…' : `Confirmar venta · ${formatMoney(total)}`}
            </Button>
          </FormActions>
        </Modal>
      )}
    </Layout>
  );
};

const Sell = ({ mode = 'empresa' }) => {
  const access = useSalesAccess(mode);
  const gate = SalesGate({ access });
  if (gate) return gate;
  return <SellForm access={access} />;
};

export default Sell;
